/* eslint-disable camelcase */
// cspell:ignore pois
import {
  SearchForFacetValuesQueryParams,
  SearchOptions,
} from "@algolia/client-search";
import algoliasearch, { SearchClient } from "algoliasearch/lite";
import invariant from "invariant";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { InstantSearch as InstantSearchBase } from "react-instantsearch-dom";
import { Location, useLocation, useNavigate } from "react-router-dom";

import config from "../../config";

interface Range {
  min?: number;
  max?: number;
}

const buildQueryParameters = (state: {
  [key: string]: any;
}): Record<string, any> => {
  const queryParameters: {
    q?: string;
    interviewer?: string | string[];
    aiInterviewer?: string | string[];
    candidate?: string | string[];
    position?: string | string[];
    client?: string | string[];
    cues?: string | string[];
    stage?: string | string[];
    type?: string | string[];
    pois?: string | string[];
    status?: string | string[];
    rating?: string | string[];
    date?: Range;
    duration?: Range;
    talk_ratio?: Range;
    longest_monologue?: Range;
    metric?: string;
    source?: string | string[];
    template_id?: string;
  } = {};

  if (state.metric) {
    queryParameters.metric = state.metric;
  }

  if (state.source) {
    queryParameters.source = state.source;
  }

  if (state.query) {
    queryParameters.q = state.query;
  }

  if (state.refinementList) {
    const {
      "interviewers.name": interviewer,
      "candidate.name": candidate,
      "ai_interviewer.name": aiInterviewer,
      "position.display_title": position,
      "position.client_name": client,
      cues,
      stage,
      type,
      pois,
      status,
      rating,
    } = state.refinementList;
    if (interviewer) {
      queryParameters.interviewer = interviewer;
    }
    if (aiInterviewer) {
      queryParameters.aiInterviewer = aiInterviewer;
    }
    if (candidate) {
      queryParameters.candidate = candidate;
    }
    if (candidate) {
      queryParameters.candidate = candidate;
    }
    if (position) {
      queryParameters.position = position;
    }
    if (client) {
      queryParameters.client = client;
    }
    if (cues) {
      queryParameters.cues = cues;
    }
    if (stage) {
      queryParameters.stage = stage;
    }
    if (pois) {
      queryParameters.pois = pois;
    }
    if (status) {
      queryParameters.status = status;
    }
    if (rating) {
      queryParameters.rating = rating;
    }
    if (type) {
      queryParameters.type = type;
    }
  }

  if (state.range) {
    const {
      call_timestamp_seconds: date,
      call_duration_seconds: duration,
      interviewer_talk_time_percentage: talk_ratio,
      interviewer_longest_monologue_seconds: longest_monologue,
    } = state.range;
    if (date) {
      queryParameters.date = date;
    }
    if (duration) {
      queryParameters.duration = duration;
    }
    if (talk_ratio) {
      queryParameters.talk_ratio = talk_ratio;
    }
    if (longest_monologue) {
      queryParameters.longest_monologue = longest_monologue;
    }
  }

  if (state.template_id) {
    queryParameters.template_id = state.template_id;
  }

  return queryParameters;
};

const searchStateToUrl = (
  location: Location,
  searchState: Record<string, unknown>
): string => {
  const currentQueryString = location.search.slice(1);
  const mergedQueryParameters = {
    // Note: The 'organization' query parameter is managed by the HomePage component, not here.
    // This separation enables proper Algolia application ID and API key configuration for sharding.
    // We simply preserve the existing organization parameter from the current URL.
    organization: qs.parse(currentQueryString).organization,
    ...buildQueryParameters(searchState),
  };
  const queryString = qs.stringify(mergedQueryParameters, {
    addQueryPrefix: true,
    arrayFormat: "repeat",
  });
  return searchState ? `${location.pathname}${queryString}` : "";
};

const urlToSearchState = (location: Location): Record<string, unknown> => {
  const {
    q = "",
    interviewer = [],
    aiInterviewer = [],
    candidate = [],
    position = [],
    client = [],
    cues = [],
    stage = [],
    type = [],
    pois = [],
    status = [],
    rating = [],
    date = {},
    duration = {},
    talk_ratio = {},
    longest_monologue = {},
    metric,
    source = [],
    template_id,
  } = qs.parse(location.search.slice(1));
  return {
    query: q,
    page: 1,
    refinementList: {
      "interviewers.name": interviewer,
      "candidate.name": candidate,
      "ai_interviewer.name": aiInterviewer,
      "position.display_title": position,
      "position.client_name": client,
      cues,
      type,
      stage,
      pois,
      status,
      rating,
    },
    range: {
      call_timestamp_seconds: date,
      call_duration_seconds: duration,
      interviewer_talk_time_percentage: talk_ratio,
      interviewer_longest_monologue_seconds: longest_monologue,
    },
    sortBy: config.algoliaIndex,
    metric,
    source,
    template_id,
  };
};

const searchClientProxy = (searchClient: SearchClient): any => ({
  // Don't make a search request with no query / filters
  ...searchClient,
  search(requests: any) {
    if (
      requests.every(
        ({
          params,
        }: {
          params: SearchForFacetValuesQueryParams & SearchOptions;
        }) => !params.query
      )
    ) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          processingTimeMS: 0,
        })),
      });
    }
    return searchClient.search(requests);
  },
});

interface InstantSearchProps {
  allowEmptyQuery?: boolean;
  children: React.ReactNode;
  searchApiKey: string;
  applicationId: string;
  updateUrl?: boolean;
}

const InstantSearch: React.FC<InstantSearchProps> = ({
  applicationId,
  searchApiKey,
  allowEmptyQuery = false,
  updateUrl = false,
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchState, setSearchState] = useState<Record<string, unknown>>(
    urlToSearchState(location)
  );

  useEffect(() => {
    setSearchState(urlToSearchState(location));
  }, [location.search]);

  invariant(config.algoliaIndex, "Missing ALGOLIA_INDEX");

  // Use state to store the search client
  const [searchClient, setSearchClient] = useState<any>(null);

  // Initialize search client in useEffect to avoid recreating it on every render
  useEffect(() => {
    if (applicationId && searchApiKey) {
      // type 'any' to facilitate workaround
      let client: any = algoliasearch(applicationId, searchApiKey);

      if (!allowEmptyQuery) {
        client = searchClientProxy(client);
      }

      // Workaround start
      // https://github.com/algolia/algoliasearch-client-javascript/issues/1035#issuecomment-592014999
      delete client.transporter.queryParameters["x-algolia-api-key"];
      const { read } = client.transporter;
      client.transporter.read = (request: any, requestOptions: any) => {
        return read(request, { apiKey: searchApiKey, ...requestOptions });
      };
      // Workaround end

      setSearchClient(client);
    }
  }, [applicationId, searchApiKey, allowEmptyQuery]);

  const onSearchStateChange = (searchState: Record<string, unknown>): void => {
    navigate(searchStateToUrl(location, searchState), {
      state: searchState,
      replace: true,
    });
    setSearchState(searchState);
  };

  return (
    <>
      {searchClient && (
        <InstantSearchBase
          searchClient={searchClient}
          indexName={config.algoliaIndex}
          searchState={updateUrl ? searchState : undefined}
          onSearchStateChange={updateUrl ? onSearchStateChange : undefined}
        >
          {children}
        </InstantSearchBase>
      )}
    </>
  );
};

const InstantSearchTest: React.FC<InstantSearchProps> = ({ children }) => (
  <>{children}</>
);

/**
 * Wraps Algolia `<InstantSearch />` component with given `searchApiKey`
 * and optionally handles reading / writing search state to URL. Renders
 * `children` as-is
 */
export default config.appEnv === "test" ? InstantSearchTest : InstantSearch;
